/**
 * Catálogo de la matriz «Menú por perfil de negocio».
 * Fuente de verdad alineada a `workspaceNavItems` (sidebar tenant).
 *
 * Inicio / Órdenes / Configuración quedan siempre visibles (no gobernados por nicho).
 */
import {
  BUSINESS_PROFILE_LABELS,
  type BusinessProfile,
} from "@/lib/business-profile";
import { navItemVisibleForServicesProfile, workspaceNavItems } from "@/components/app-nav";

/** Paths del sidebar que nunca se ocultan por perfil. */
export const PROFILE_NAV_ALWAYS_ON = new Set([
  "/app/dashboard",
  "/app/ordenes",
  "/app/settings",
]);

export type ProfileNavFeatureDef = {
  path: string;
  label: string;
  /** Grupo visual en el panel admin. */
  group: "catalogo" | "comercio" | "ops" | "experiencia";
  /** Perfiles con sentido por defecto (onlyProfiles). Ausente = todos. */
  defaultProfiles?: readonly BusinessProfile[];
};

const GROUP_BY_PATH: Record<string, ProfileNavFeatureDef["group"]> = {
  "/app/catalogacion": "catalogo",
  "/app/media": "catalogo",
  "/app/digital": "catalogo",
  "/app/eventos": "catalogo",
  "/app/experience": "experiencia",
  "/app/clientes": "comercio",
  "/app/fidelizacion": "comercio",
  "/app/descuentos": "comercio",
  "/app/ruleta": "comercio",
  "/app/pagos": "comercio",
  "/app/facturacion": "comercio",
  "/app/metricas": "comercio",
  "/app/operaciones": "ops",
  "/app/inventario": "ops",
  "/app/ingredientes": "ops",
  "/app/produccion": "ops",
  "/app/logistica": "ops",
  "/app/pos": "ops",
  "/app/salon": "ops",
  "/app/reservas": "ops",
  "/app/hardware": "ops",
  "/app/agenda": "ops",
  "/app/turnos": "ops",
  "/app/profesionales": "ops",
  "/app/servicios": "catalogo",
  "/app/clases": "catalogo",
  "/app/horarios": "ops",
  "/app/packs": "comercio",
  "/app/lista-espera": "ops",
  "/app/formularios": "ops",
  "/app/resenas": "comercio",
  "/app/liquidaciones": "comercio",
  "/app/actividad-cobros": "comercio",
  "/app/recursos": "catalogo",
  "/app/metricas-servicios": "comercio",
};

export const PROFILE_NAV_GROUP_LABELS: Record<ProfileNavFeatureDef["group"], string> = {
  catalogo: "Catálogo & contenido",
  comercio: "Comercio & clientes",
  ops: "Operaciones",
  experiencia: "Experiencia",
};

/** Features gobernables = todos los ítems del sidebar excepto always-on. */
export const PROFILE_NAV_FEATURES: ProfileNavFeatureDef[] = workspaceNavItems
  .filter((item) => !PROFILE_NAV_ALWAYS_ON.has(item.to))
  .map((item) => ({
    path: item.to,
    label: item.label,
    group: GROUP_BY_PATH[item.to] ?? "ops",
    defaultProfiles: item.onlyProfiles,
  }));

export const PROFILE_NAV_PATHS = PROFILE_NAV_FEATURES.map((f) => f.path);

export const PROFILE_NAV_PROFILES = Object.keys(BUSINESS_PROFILE_LABELS) as BusinessProfile[];

export function profileNavLabel(profile: string): string {
  return BUSINESS_PROFILE_LABELS[profile as BusinessProfile] ?? profile;
}

export function featureLabel(path: string): string {
  return PROFILE_NAV_FEATURES.find((f) => f.path === path)?.label ?? path;
}

/** Default cuando no hay fila en DB — mismo criterio que `onlyProfiles` del sidebar. */
export function defaultProfileNavEnabled(
  profile: string,
  path: string,
): boolean {
  const feat = PROFILE_NAV_FEATURES.find((f) => f.path === path);
  if (!feat) return false;
  if (profile === "servicios") {
    return navItemVisibleForServicesProfile({
      to: path,
      label: feat.label,
      icon: undefined as never,
      onlyProfiles: feat.defaultProfiles ? [...feat.defaultProfiles] : undefined,
    });
  }
  if (!feat.defaultProfiles || feat.defaultProfiles.length === 0) return true;
  return feat.defaultProfiles.includes(profile as BusinessProfile);
}

export function isValidProfileNavProfile(profile: string): boolean {
  return profile in BUSINESS_PROFILE_LABELS;
}

export function isValidProfileNavPath(path: string): boolean {
  return PROFILE_NAV_PATHS.includes(path);
}
